import React, { useState, useEffect } from 'react';
import { 
  Search, Users, Palette, TrendingUp, Award, Eye, CheckCircle,
  ChevronUp, ChevronDown, Filter, Download, MoreVertical,
  Phone, Instagram, Globe, Activity, Crown
} from 'lucide-react';
import { fetchArtistsEnhanced, exportArtistData } from '../utils/artistDashboardApi';
import '../styles/ImprovedArtistDashboard.css';
import { Link } from 'react-router-dom';

const ImprovedArtistDashboard = () => {
  // State management
  const [data, setData] = useState({ artists: [], totals: {}, pagination: {} });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [memberFilter, setMemberFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedArtists, setSelectedArtists] = useState([]);



  // Column mapping for sorting
  const columnMap = {
    'name': 'nickname',
    'email': 'email',
    'artworkCount': 'artworkCount',
    'accountAge': 'created_at'
  };

  // Color map for stat cards
  const colorMap = {
    indigo: 'text-indigo-600 bg-indigo-50',
    green: 'text-green-600 bg-green-50',
    purple: 'text-purple-600 bg-purple-50',
    yellow: 'text-yellow-600 bg-yellow-50',
    blue: 'text-blue-600 bg-blue-50',
    emerald: 'text-emerald-600 bg-emerald-50',
  };

  // Fetch data when filters change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const offset = (currentPage - 1) * pageSize;
        const response = await fetchArtistsEnhanced(
          searchQuery,
          pageSize,
          offset,
          sortColumn,
          sortDirection,
          memberFilter
        );

        console.log('Fetched artist data:', response);
        setData(response);

      } catch (error) {
        console.error('Error fetching artist data:', error);
        setData({ artists: [], totals: {}, pagination: {} });
      }
      setLoading(false);
    };

    fetchData();
  }, [searchQuery, currentPage, sortColumn, sortDirection, memberFilter, pageSize]);

  // Handle sorting
  const handleSort = (column) => {
    const sqlColumn = columnMap[column] || column;
    if (sortColumn === sqlColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(sqlColumn);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Handle artist selection
  const handleSelectArtist = (artistEmail) => {
    setSelectedArtists(prev => 
      prev.includes(artistEmail)
        ? prev.filter(email => email !== artistEmail)
        : [...prev, artistEmail]
    );
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedArtists.length === data.artists.length) {
      setSelectedArtists([]);
    } else {
      setSelectedArtists(data.artists.map(artist => artist.email));
    }
  };

  // Handle export
  const handleExport = async () => {
    if (selectedArtists.length === 0) return;
    
    try {
      await exportArtistData(selectedArtists);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Helper functions
  const getCloudinaryUrl = (publicId, options = {}) => {
    const baseUrl = 'https://res.cloudinary.com/vibrantcast/image/upload';
    const transformations = Object.entries(options)
      .map(([key, value]) => `${key}_${value}`)
      .join(',');
    
    return `${baseUrl}/${transformations ? transformations + '/' : ''}${publicId}`;
  };

  const getActivityColor = (activity) => {
    if (activity >= 10) return 'bg-green-100 text-green-800';
    if (activity >= 5) return 'bg-yellow-100 text-yellow-800';
    if (activity >= 1) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getActivityLabel = (activity) => {
    if (activity >= 10) return 'Very Active';
    if (activity >= 5) return 'Active';
    if (activity >= 1) return 'Some Activity';
    return 'No Activity';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Stats Cards Component
  const StatsCards = () => (
    <div className="stats-grid">
      <div className="stat-card clickable">
        <div className="stat-content">
          <div className="stat-icon">
            <Users className="w-6 h-6" />
          </div>
          <div className="stat-details">
            <div className="stat-value">{data.globalTotals?.totalArtists?.toLocaleString() || 0}</div>
            <div className="stat-label">Total Artists</div>
          </div>
        </div>
      </div>
      
      <div className="stat-card clickable">
        <div className="stat-content">
          <div className="stat-icon">
            <CheckCircle className="w-6 h-6" />
          </div>
          <div className="stat-details">
            <div className="stat-value">{data.globalTotals?.memberCount?.toLocaleString() || 0}</div>
            <div className="stat-label">Members</div>
          </div>
        </div>
      </div>
      
      <div className="stat-card clickable">
        <div className="stat-content">
          <div className="stat-icon">
            <Palette className="w-6 h-6" />
          </div>
          <div className="stat-details">
            <div className="stat-value">{data.globalTotals?.totalArtworks?.toLocaleString() || 0}</div>
            <div className="stat-label">Total Artworks</div>
          </div>
        </div>
      </div>
      
      <div className="stat-card clickable">
        <div className="stat-content">
          <div className="stat-icon">
            <Activity className="w-6 h-6" />
          </div>
          <div className="stat-details">
            <div className="stat-value">{data.globalTotals?.activeArtists?.toLocaleString() || 0}</div>
            <div className="stat-label">Active (30 days)</div>
          </div>
        </div>
      </div>
    </div>
  );

  const UserRow = ({ artist, isSelected, onSelect }) => {
    const avatarUrl = artist.profileImg ? getCloudinaryUrl(artist.profileImg, { width: 40, height: 40 }) : null;
    const initials = (artist.nickname || `${artist.firstName || ''} ${artist.lastName || ''}`)
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    const artworkDetails = artist.artworkDetails || {};

    return (
      <tr className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50 border-blue-200' : ''}`}>
        <td className="px-6 py-4">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(artist.email)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
        </td>
        
        {/* User Info */}
        <td className="px-6 py-4">
          <div className="flex items-center">
            {avatarUrl ? (
              <img
                className="user-avatar"
                src={avatarUrl}
                alt={artist.nickname || 'Artist'}
              />
            ) : (
              <div className="user-avatar user-avatar-fallback">
                <span className="user-avatar-initials">{initials}</span>
              </div>
            )}
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">
                {artist.nickname || `${artist.firstName || ''} ${artist.lastName || ''}`.trim()}
              </div>
              {artist.handle && (
                <div className="text-sm text-gray-500">{artist.handle}</div>
              )}
            </div>
          </div>
        </td>

        {/* Contact */}
        <td className="px-6 py-4">
          <div className="text-sm text-gray-900">{artist.email}</div>
          <div className="flex items-center space-x-2 mt-1">
            {artist.mobilePhone && (
              <Phone className="w-3 h-3 text-gray-400" title="Has phone" />
            )}
            {artist.instagram && (
              <Instagram className="w-3 h-3 text-gray-400" title="Has Instagram" />
            )}
            {artist.bio && (
              <Globe className="w-3 h-3 text-gray-400" title="Has bio" />
            )}
          </div>
        </td>

        {/* Membership */}
        <td className="px-6 py-4">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            artist.member === true 
              ? 'bg-green-100 text-green-800' 
              : artist.member === false 
                ? 'bg-yellow-100 text-yellow-800' 
                : 'bg-gray-100 text-gray-800'
          }`}>
            {artist.member === true ? 'Member' : artist.member === false ? 'Non-Member' : 'Unknown'}
          </span>
        </td>

        {/* Type */}
        <td className="px-6 py-4">
          <span className="text-sm text-gray-900 capitalize">
            {artist.selfIdentifiedAs || 'Not specified'}
          </span>
          {artist.commissions && (
            <div className="text-xs text-blue-600 mt-1">
              <Award className="w-3 h-3 inline mr-1" />
              Commissions
            </div>
          )}
        </td>

        {/* Artworks */}
        <td className="px-6 py-4">
          <div className="text-sm font-medium text-gray-900">
            {artist.artworkCount || 0}
          </div>
          {artworkDetails.pricedArtworks > 0 && (
            <div className="text-xs text-green-600">
              {artworkDetails.pricedArtworks} priced
            </div>
          )}
        </td>

        {/* Activity */}
        <td className="px-6 py-4">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            getActivityColor(artworkDetails.artistActivity)
          }`}>
            {getActivityLabel(artworkDetails.artistActivity)}
          </span>
          {artworkDetails.artworksLast30Days > 0 && (
            <div className="text-xs text-green-600 mt-1">
              +{artworkDetails.artworksLast30Days} this month
            </div>
          )}
        </td>

        {/* Joined */}
        <td className="px-6 py-4 text-sm text-gray-500">
          {formatDate(artist.created_at)}
          <div className="text-xs text-gray-400">
            {artist.accountAge} days ago
          </div>
        </td>

        {/* Status */}
        <td className="px-6 py-4">
          <div className="flex flex-col space-y-1">
            {artist.agreedToTerms ? (
              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Terms Agreed
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                Terms Pending
              </span>
            )}
          </div>
        </td>

        {/* Actions */}
        <td className="px-6 py-4">
          <div className="flex items-center space-x-2">
            {artist.handle && (
              <Link
                to={`/admin/artist/${artist.handle}`}
                className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
              >
                View
              </Link>
            )}
            <button className="text-gray-400 hover:text-gray-600">
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const SortableHeader = ({ column, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortColumn === columnMap[column] && (
          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading artist data...</p>
      </div>
    );
  }

  return (
    <div className="artist-dashboard">
      <div className="dashboard-container">
        {/* Enhanced Stats Cards */}
        <StatsCards />
        
        {/* Main Content */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          {/* Filters and Search */}
          <div className="filters-container">
            <div className="filters-row">
              <div className="filters-left">
                <div className="search-group">
                  <input
                    id="artist-search"
                    type="text"
                    placeholder="Search artists…"
                    className="search-input prominent-search-input"
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  />
                </div>
              </div>
              <div className="filters-right">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="filter-select"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                {selectedArtists.length > 0 && (
                  <>
                    <span className="text-sm text-gray-600">
                      {selectedArtists.length} selected
                    </span>
                    <button
                      onClick={handleExport}
                      className="action-button secondary"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </button>
                  </>
                )}
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {data.artists.length} of {data.pagination.total || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Table */}
        <div className="table-container">
          <table className="artist-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedArtists.length === data.artists.length && data.artists.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <SortableHeader column="name">Name</SortableHeader>
                <SortableHeader column="email">Email</SortableHeader>
                <th>Membership</th>
                <th>Type</th>
                <SortableHeader column="artworkCount">Artworks</SortableHeader>
                <th>Activity</th>
                <SortableHeader column="accountAge">Joined</SortableHeader>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.artists.map((artist) => (
                <UserRow
                  key={artist.email}
                  artist={artist}
                  isSelected={selectedArtists.includes(artist.email)}
                  onSelect={handleSelectArtist}
                />
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {data.pagination && data.pagination.total > pageSize && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!data.pagination.hasMore}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, data.pagination.total)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium">{data.pagination.total}</span>
                    {' '}results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, Math.ceil(data.pagination.total / pageSize)) }, (_, i) => {
                      const pageNum = Math.max(1, currentPage - 2) + i;
                      const totalPages = Math.ceil(data.pagination.total / pageSize);
                      if (pageNum > totalPages) return null;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === currentPage
                              ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!data.pagination.hasMore}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImprovedArtistDashboard;